import Highcharts from 'highcharts';
import { paletaSeries } from '../../../../assets/estilos/colores';

/**
 * Tema de Highcharts armado con los colores de la app.
 *
 * Reemplaza al `dark-unica` que se aplicaba siempre: pintaba todos los
 * gráficos con fondo gris degradado y texto claro aunque la app estuviera en
 * tema claro, y en tema oscuro tampoco usaba la paleta de la app.
 *
 * Los colores salen de las CSS variables del tema activo (`--fg-color`,
 * `--bg-color-solid`, `--accent-color`, `--primary-color`), así que siguen
 * solos cualquier cambio de paleta. Se aplica con `setOptions` —defaults
 * globales— para que alcance también a los gráficos con varios ejes y a los
 * stockChart (navigator, rangeSelector, scrollbar); lo que declare cada
 * gráfico en sus opciones sigue ganando.
 */
/** Familia tipográfica del tema para Highcharts (lee `--font-regular`). */
export function fuenteChart(): string {
  const fuente = getComputedStyle(document.body)
    .getPropertyValue('--font-regular')
    .trim();
  return fuente || 'monserrat-regular, sans-serif';
}

export function aplicarTemaChart(): void {
  const css = getComputedStyle(document.body);
  const leer = (variable: string, porDefecto: string) =>
    css.getPropertyValue(variable).trim() || porDefecto;

  const fg = leer('--fg-color', '#000000');
  const bg = leer('--bg-color-solid', '#ffffff');
  const acento = leer('--accent-color', '#2b908f');
  const primario = leer('--primary-color', '#7798bf');
  const alfa = (color: string, opacidad: number) =>
    Highcharts.color(color).setOpacity(opacidad).get('rgba') as string;

  const texto = { color: fg };
  const textoSuave = { color: alfa(fg, 0.7) };
  const grilla = alfa(fg, 0.15);
  const linea = alfa(fg, 0.3);
  const eje: Highcharts.XAxisOptions = {
    gridLineColor: grilla,
    minorGridLineColor: alfa(fg, 0.08),
    lineColor: linea,
    tickColor: linea,
    labels: { style: texto },
    title: { style: textoSuave },
  };
  const boton = {
    fill: alfa(fg, 0.08),
    stroke: 'none',
    style: texto,
    states: {
      hover: { fill: alfa(acento, 0.35), style: texto },
      select: { fill: acento, style: { color: bg } },
    },
  };

  Highcharts.setOptions({
    colors: paletaSeries([
      acento,
      primario,
      '#90ee7e',
      '#f45b5b',
      '#f7a35c',
      '#aaeeee',
      '#ff0066',
      '#eeaaee',
      '#55BF3B',
      '#DF5353',
    ]),
    chart: {
      // El fondo lo pone el contenedor (card, panel): así toma el del tema.
      backgroundColor: 'transparent',
      plotBorderColor: linea,
      style: { fontFamily: fuenteChart() },
    },
    title: { style: { ...texto, textTransform: 'none' } },
    subtitle: { style: { ...textoSuave, textTransform: 'none' } },
    xAxis: eje,
    yAxis: { ...eje, tickWidth: 1 } as Highcharts.YAxisOptions,
    tooltip: {
      backgroundColor: alfa(bg, 0.95),
      borderColor: linea,
      style: texto,
    },
    plotOptions: {
      series: {
        dataLabels: { color: fg, style: { textOutline: 'none' } },
        marker: { lineColor: bg },
      },
    },
    legend: {
      backgroundColor: 'transparent',
      itemStyle: texto,
      itemHoverStyle: { color: acento },
      itemHiddenStyle: { color: alfa(fg, 0.35) },
      title: { style: texto },
    },
    credits: { style: { color: alfa(fg, 0.5) } },
    noData: { style: texto },
    // El aviso de carga sobre un gráfico ya dibujado (`showLoading`): el blanco de
    // Highcharts se veía como un flash en tema oscuro.
    loading: {
      style: { backgroundColor: alfa(bg, 0.6) },
      labelStyle: { ...texto, fontWeight: 'normal' },
    },
    navigation: {
      buttonOptions: { symbolStroke: fg, theme: { fill: 'transparent' } },
    },
    rangeSelector: {
      buttonTheme: boton,
      inputBoxBorderColor: linea,
      inputStyle: { backgroundColor: 'transparent', color: fg },
      labelStyle: textoSuave,
    },
    navigator: {
      handles: { backgroundColor: bg, borderColor: linea },
      outlineColor: linea,
      maskFill: alfa(acento, 0.15),
      series: { color: acento, lineColor: acento },
      xAxis: { gridLineColor: grilla, labels: { style: textoSuave } },
    },
    scrollbar: {
      barBackgroundColor: alfa(fg, 0.25),
      barBorderColor: 'transparent',
      buttonArrowColor: fg,
      buttonBackgroundColor: alfa(fg, 0.1),
      buttonBorderColor: 'transparent',
      rifleColor: fg,
      trackBackgroundColor: alfa(fg, 0.05),
      trackBorderColor: 'transparent',
    },
  } as Highcharts.Options);
}
